import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { assertIsClientContext, context } from '@adornis/baseql/server/context.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { setUserOnContext } from '@adornis/users/server/baseqlContextHelper.js';
import { changePasswordWithToken, getPasswordResetLink } from '@adornis/users/server/password.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { sendPasswordChangedEmail, sendPasswordResetEmail } from '../../server/utils/email.js';
import { revokeOtherSessions, revokeResetTickets, tagResetTicket } from '../../server/utils/auth-revocation.js';
import { isPendingRegistration, markEmailVerified } from './email-verification.js';

/**
 * jsonwebtoken reads a numeric expiresIn as SECONDS, but the library's default is 24 h in milliseconds, i.e.
 * about 2.7 years. The reset link therefore always gets an explicit lifetime. Keep in step with sendPasswordResetEmail.
 */
const RESET_LINK_TTL_SECONDS = 60 * 60; // 1 hour

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const requestPasswordResetMutation = registerMutation({
  operationName: 'requestPasswordResetMutation',
  type: () => Boolean,
  params: [
    { name: 'email', type: () => String },
    // Only picks the wording of the mail ("ändern" from the profile, "zurücksetzen" from the login modal).
    { name: 'isChange', type: () => Boolean },
  ],
  resolve: (email: string, isChange: boolean) => async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return true;

    const userCollection = await getRawCollection(AdornisUser._collectionName);
    const user = await userCollection.findOne({
      email: { $regex: new RegExp(`^${escapeRegExp(normalizedEmail)}$`, 'i') },
    });

    // Intentionally return success for unknown emails to prevent account enumeration.
    if (!user) return true;
    // Nor a link for a pending registration (email-verification.ts): whoever only has the mailbox, e.g. after a typo
    // in the address, would set a password and, by using the link, verify an account someone else registered.
    if (isPendingRegistration(user)) return true;

    // Not awaited: the answer must take as long for an existing account as for an unknown address, or its timing
    // would tell who uses Gradias. A failure is logged here (and with the mail context in sendMail), never answered.
    const userID = String(user._id);
    void issuePasswordReset(user, isChange).catch(err =>
      logger.error({ err, userID, context: 'AUTH' }, 'Passwort-Reset-Link konnte nicht verschickt werden'),
    );
    return true;
  },
});

async function issuePasswordReset(user: { _id: unknown; _class?: unknown; email?: unknown }, isChange: boolean) {
  const frontendResetPath = new URL('/auth/reset-password', baseConfig.get('ROOT_URL')).toString();
  const userID = String(user._id);
  const userClass = typeof user._class === 'string' ? user._class : AdornisUser._class;

  // Only the newest link works: an older one in an inbox must not stay a key to the account.
  await revokeResetTickets(userID);
  const resetLink = await getPasswordResetLink(frontendResetPath, {
    userID,
    userClass,
    ttu: 1,
    ttl: RESET_LINK_TTL_SECONDS,
  });
  await tagResetTicket(resetLink, userID);

  // The stored address, not the typed one: the lookup above ignores case and may match a differently written one.
  await sendPasswordResetEmail(String(user.email), resetLink, userID, isChange);
}

// Replaces the library's changePasswordWithToken mutation so the user gets a confirmation mail.
export const resetPasswordWithTokenMutation = registerMutation({
  operationName: 'resetPasswordWithTokenMutation',
  type: () => Boolean,
  params: [
    { name: 'token', type: () => String },
    { name: 'newPassword', type: () => String },
    { name: 'login', type: () => Boolean },
  ],
  resolve: (token: string, newPassword: string, login: boolean) => async () => {
    assertIsClientContext(context);
    const user = await changePasswordWithToken(token, newPassword);
    // The reset link went to the account's address, so using it confirms that address as well.
    await markEmailVerified(user._id);
    if (login) await setUserOnContext(user);

    // Like the mail below: the password is already changed, so a failed revocation is logged, not thrown.
    try {
      await revokeResetTickets(String(user._id));
      await revokeOtherSessions(String(user._id));
    } catch (err) {
      logger.error(
        { err, userID: String(user._id), context: 'AUTH' },
        'Revoking links and sessions after reset failed',
      );
    }

    // The password is already changed at this point, so a failed mail must not fail the reset.
    // Errors are logged inside sendPasswordChangedEmail.
    const userID = String(user._id);
    if (typeof user.email === 'string' && user.email) {
      void sendPasswordChangedEmail(user.email, userID).catch(() => undefined);
    } else {
      logger.warn(
        { userID, context: 'MAILER' },
        'Passwort geändert, aber keine E-Mail-Adresse für Bestätigung vorhanden',
      );
    }

    return true;
  },
});
